import React, { useState, useEffect } from 'react';
import { getSafeDisplayName } from '../utils/profanityFilter';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, getDoc, doc, updateDoc, addDoc, deleteDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { checkAllSystems, getStatusSummary, checkFirebaseStatus, checkCloudFunctionsStatus, checkOpenAIStatus } from '../services/statusService';

export default function AdminDashboard({ user, darkMode }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalRevenue: 0,
    storageUsed: 0,
  });
  const [users, setUsers] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [messageFeedback, setMessageFeedback] = useState([]);
  const [reportedMessages, setReportedMessages] = useState([]);
  const [issues, setIssues] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [userUpdates, setUserUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [systemStatus, setSystemStatus] = useState({
    firebase: 'checking',
    openai: 'checking',
    agents: 'checking',
    blocks: 'checking',
    functions: 'checking'
  });
  const [systemDetails, setSystemDetails] = useState({
    collections: [],
    cloudFunctions: [],
    agents: [],
    totalDocs: 0,
    lastChecked: null
  });
  const [autoReconnect, setAutoReconnect] = useState(true);
  const [reconnectInterval, setReconnectInterval] = useState(null);
  const [showViewAsDropdown, setShowViewAsDropdown] = useState(false);

  const auth = getAuth();
  const db = getFirestore();
  const functions = getFunctions();

  // Check if user is admin by looking in admins collection
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }

      try {
        const adminDocRef = doc(db, 'admins', user.uid);
        const adminDoc = await getDoc(adminDocRef);
        
        if (adminDoc.exists() && adminDoc.data().isAdmin === true) {
          setIsAdmin(true);
          console.log('✅ Admin access granted for:', user.email);
        } else {
          setIsAdmin(false);
          console.log('❌ Admin access denied for:', user.email);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
      
      setCheckingAdmin(false);
    };

    checkAdminStatus();
  }, [user, db]);

  useEffect(() => {
    if (isAdmin && !checkingAdmin) {
      loadAdminData();
      
      // Start auto-reconnect monitoring if enabled
      if (autoReconnect) {
        startAutoReconnect();
      }
    }
    
    return () => {
      // Cleanup: Stop auto-reconnect when component unmounts
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
      }
    };
  }, [isAdmin, checkingAdmin, autoReconnect]);
  
  // Auto-reconnect monitoring: check systems every 30 seconds
  const startAutoReconnect = () => {
    console.log('🔄 Auto-reconnect monitoring started');
    
    // Clear any existing interval
    if (reconnectInterval) {
      clearInterval(reconnectInterval);
    }
    
    // Set up new interval to check every 30 seconds
    const interval = setInterval(async () => {
      console.log('🔄 Auto-reconnect: Checking system status...');
      
      try {
        const results = await checkAllSystems();
        const statusSummary = getStatusSummary(results);
        
        // Check if any system is in error state
        const hasErrors = Object.values(statusSummary).some(status => status === 'error');
        
        if (hasErrors) {
          console.log('⚠️ Auto-reconnect: Errors detected, attempting reconnection...');
          await attemptReconnection(statusSummary);
        } else {
          console.log('✅ Auto-reconnect: All systems operational');
        }
        
        setSystemStatus(statusSummary);
      } catch (error) {
        console.error('❌ Auto-reconnect check failed:', error);
      }
    }, 30000); // 30 seconds
    
    setReconnectInterval(interval);
  };
  
  // Attempt to reconnect failed systems
  const attemptReconnection = async (currentStatus) => {
    console.log('🔧 Attempting to reconnect failed systems...');
    
    const reconnectAttempts = [];
    
    // Try to reconnect each failed system
    if (currentStatus.firebase === 'error') {
      console.log('🔧 Reconnecting Firebase...');
      reconnectAttempts.push(checkFirebaseStatus());
    }
    
    if (currentStatus.functions === 'error') {
      console.log('🔧 Reconnecting Cloud Functions...');
      reconnectAttempts.push(checkCloudFunctionsStatus());
    }
    
    if (currentStatus.openai === 'error') {
      console.log('🔧 Reconnecting OpenAI...');
      reconnectAttempts.push(checkOpenAIStatus());
    }
    
    if (reconnectAttempts.length > 0) {
      const results = await Promise.allSettled(reconnectAttempts);
      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.status === 'connected').length;
      
      if (successCount > 0) {
        console.log(`✅ Reconnection successful for ${successCount}/${reconnectAttempts.length} systems`);
      } else {
        console.log(`❌ Reconnection failed for all ${reconnectAttempts.length} systems`);
      }
    }
  };
  
  // Force reconnect all systems manually
  const forceReconnect = async () => {
    console.log('🔄 Force reconnect initiated...');
    
    setSystemStatus({
      firebase: 'checking',
      openai: 'checking',
      agents: 'checking',
      blocks: 'checking',
      functions: 'checking'
    });
    
    try {
      const results = await checkAllSystems();
      const statusSummary = getStatusSummary(results);
      await attemptReconnection(statusSummary);
      
      // Recheck after reconnection attempt
      const recheckResults = await checkAllSystems();
      const recheckSummary = getStatusSummary(recheckResults);
      setSystemStatus(recheckSummary);
      
      console.log('✅ Force reconnect complete:', recheckSummary);
    } catch (error) {
      console.error('❌ Force reconnect failed:', error);
      setSystemStatus({
        firebase: 'error',
        openai: 'error',
        agents: 'error',
        blocks: 'error',
        functions: 'error'
      });
    }
  };

  const loadAdminData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadUsers(),
        loadPromoCodes(),
        loadFeedback(),
        loadIssues(),
        loadSupportTickets(),
        loadUserUpdates(),
        checkSystemStatus(),
      ]);
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
    setLoading(false);
  };

  const checkSystemStatus = async () => {
    console.log('🔍 Checking all system statuses...');
    
    try {
      // Set all to checking state
      setSystemStatus({
        firebase: 'checking',
        openai: 'checking',
        agents: 'checking',
        blocks: 'checking',
        functions: 'checking'
      });
      
      // Check all systems using the status service
      const results = await checkAllSystems();
      const statusSummary = getStatusSummary(results);
      
      // Update UI with results
      setSystemStatus(statusSummary);
      
      // Load detailed system information
      await loadSystemDetails();
      
      console.log('✅ System status check complete:', statusSummary);
    } catch (error) {
      console.error('❌ System status check failed:', error);
      setSystemStatus({
        firebase: 'error',
        openai: 'error',
        agents: 'error',
        blocks: 'error',
        functions: 'error'
      });
    }
  };

  // Load detailed system information
  const loadSystemDetails = async () => {
    console.log('📊 Loading detailed system information...');
    
    try {
      const details = {
        collections: [],
        cloudFunctions: [
          { name: 'fidoraChat', status: 'active', purpose: 'Main chat endpoint (callable)' },
          { name: 'fidoraChatHttp', status: 'active', purpose: 'HTTP chat endpoint' },
          { name: 'fidoraTTS', status: 'active', purpose: 'Text-to-speech generation' },
          { name: 'fidoraTranscribe', status: 'active', purpose: 'Audio transcription' },
          { name: 'parseResume', status: 'active', purpose: 'Resume parsing & ATS optimization' },
          { name: 'analyzeSTAR', status: 'active', purpose: 'Interview answer analysis' },
          { name: 'fidoraGetCareerMemory', status: 'active', purpose: 'Career memory retrieval' },
          { name: 'fidoraUpdateMemoryLayer', status: 'active', purpose: 'Memory layer updates' },
          { name: 'fidorasendpasswordreset', status: 'active', purpose: 'Branded password reset emails' },
          { name: 'saveNotificationPrefs', status: 'active', purpose: 'Notification preferences' },
          { name: 'getNotificationPrefs', status: 'active', purpose: 'Get notification settings' }
        ],
        agents: [
          { name: 'Nova', role: 'Caring guide', tier: 'free', voice: 'nova' },
          { name: 'Atlas', role: 'Strategic leader', tier: 'coach', voice: 'onyx' },
          { name: 'Sage', role: 'Deep wisdom', tier: 'mentor', voice: 'echo' },
          { name: 'Phoenix', role: 'Dynamic energy', tier: 'mentor', voice: 'fable' },
          { name: 'Aurora', role: 'Polished grace', tier: 'mentor', voice: 'shimmer' },
          { name: 'Catalyst', role: 'Change agent', tier: 'mentor', voice: 'alloy' }
        ],
        functionsHealth: null,
        mcp: { url: '', status: 'unknown', info: null },
        totalDocs: 0,
        lastChecked: new Date()
      };

      // Get all collections and count documents
      const collectionsToCheck = [
        'users', 'admins', 'coaching_prompts', 'variables', 'knowledge_base',
        'messageFeedback', 'reportedMessages', 'generalFeedback', 'feedback',
        'issues', 'supportTickets', 'promoCodes', 'usage', 'conversations',
        'identity', 'narrative', 'coaching', 'emotional', 'engagement',
        'voice_sessions', 'voice_policy', 'voice_topups', 'freeTrials'
      ];

      let totalCount = 0;

      for (const collName of collectionsToCheck) {
        try {
          const snapshot = await getDocs(collection(db, collName));
          const count = snapshot.size;
          totalCount += count;
          
          details.collections.push({
            name: collName,
            count: count,
            status: count > 0 ? 'active' : 'empty'
          });
        } catch (error) {
          console.warn(`⚠️ Could not load collection ${collName}:`, error.message);
          details.collections.push({
            name: collName,
            count: 0,
            status: 'error'
          });
        }
      }

      details.totalDocs = totalCount;

      // Fetch Functions health
      try {
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'fidora-72b3d';
        const region = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'us-central1';
        const healthUrl = `https://${region}-${projectId}.cloudfunctions.net/health`;
        const resp = await fetch(healthUrl, { method: 'GET' });
        if (resp.ok) {
          details.functionsHealth = await resp.json();
          if (Array.isArray(details.functionsHealth.agents)) {
            details.agents = details.functionsHealth.agents.map((a) => (
              typeof a === 'string' ? { name: a, role: 'Coach', tier: 'free', voice: 'default' } : a
            ));
          }
        }
      } catch (e) {
        console.warn('⚠️ Could not load Functions health:', e?.message || e);
      }

      // Check MCP server (Firebase-native)
      try {
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'fidora-72b3d';
        const region = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'us-central1';
        const mcpUrl = `https://${region}-${projectId}.cloudfunctions.net/api`;
        details.mcp.url = mcpUrl;
        const ctrl = new AbortController();
        const id = setTimeout(() => ctrl.abort(), 5000);
        // Check MCP multi-agent endpoint (Firebase-native)
        const mcpResp = await fetch(`${mcpUrl}/multi-agent`, { 
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agentTypes: ['career'], query: 'health check', context: { source: 'admin-dashboard' } }),
          signal: ctrl.signal 
        });
        clearTimeout(id);
        if (mcpResp.ok) {
          details.mcp.status = 'active';
          try { 
            const mcpData = await mcpResp.json(); 
            details.mcp.info = { 
              version: '1.0-firebase-native', 
              agents: mcpData.agentsUsed || ['career', 'resume', 'interview', 'salary', 'skills', 'leadership'] 
            };
          } catch {}
          if (!Array.isArray(details.functionsHealth?.agents) && Array.isArray(details.mcp.info?.agents)) {
            details.agents = details.mcp.info.agents.map((a) => (
              typeof a === 'string' ? { name: a, role: 'Coach', tier: 'free', voice: 'default' } : a
            ));
          }
        } else {
          details.mcp.status = 'error';
        }
      } catch (e) {
        details.mcp.status = 'error';
      }
      
      setSystemDetails(details);
      console.log('✅ System details loaded:', details);
    } catch (error) {
      console.error('❌ Failed to load system details:', error);
    }
  };

  const loadStats = async () => {
    try {
      // Get all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate stats
      const totalUsers = usersData.length;
      const activeUsers = usersData.filter(u => {
        const lastActivity = u.usage?.lastActivity?.toDate?.() || new Date(0);
        const daysSinceActivity = (Date.now() - lastActivity) / (1000 * 60 * 60 * 24);
        return daysSinceActivity < 7;
      }).length;

      // Calculate revenue (based on subscriptions)
      const revenue = usersData.reduce((sum, u) => {
        if (u.subscriptionTier === 'advisor') return sum + 9.99;
        if (u.subscriptionTier === 'coach') return sum + 14.99;
        if (u.subscriptionTier === 'mentor') return sum + 24.99;
        return sum;
      }, 0);

      // Calculate storage (approximate)
      const storage = usersData.reduce((sum, u) => {
        return sum + (u.usage?.textTokensUsed || 0) * 4; // ~4 bytes per token
      }, 0) / (1024 * 1024); // Convert to MB

      setStats({
        totalUsers,
        activeUsers,
        totalRevenue: revenue.toFixed(2),
        storageUsed: storage.toFixed(2),
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const usersSnapshot = await getDocs(query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(100)
      ));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadPromoCodes = async () => {
    try {
      const promoSnapshot = await getDocs(collection(db, 'promoCodes'));
      const promoData = promoSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPromoCodes(promoData);
    } catch (error) {
      console.error('Error loading promo codes:', error);
    }
  };

  const loadFeedback = async () => {
    try {
      // Load general feedback
      const feedbackSnapshot = await getDocs(query(
        collection(db, 'generalFeedback'),
        orderBy('timestamp', 'desc'),
        limit(50)
      ));
      const feedbackData = feedbackSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setFeedback(feedbackData);

      // Load message feedback (thumbs up/down)
      const msgFeedbackSnapshot = await getDocs(query(
        collection(db, 'messageFeedback'),
        orderBy('timestamp', 'desc'),
        limit(100)
      ));
      const msgFeedbackData = msgFeedbackSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessageFeedback(msgFeedbackData);

      // Load reported messages
      const reportedSnapshot = await getDocs(query(
        collection(db, 'reportedMessages'),
        orderBy('reportedAt', 'desc'),
        limit(100)
      ));
      const reportedData = reportedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReportedMessages(reportedData);
    } catch (error) {
      console.error('Error loading feedback:', error);
    }
  };

  // Delete message feedback
  const deleteMessageFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this message feedback?')) {
      return;
    }
    
    try {
      console.log('🗑️ Attempting to delete feedback with ID:', feedbackId);
      const feedbackRef = doc(db, 'messageFeedback', feedbackId);
      await deleteDoc(feedbackRef);
      setMessageFeedback(prevFeedback => prevFeedback.filter(fb => fb.id !== feedbackId));
      console.log('✅ Message feedback deleted successfully:', feedbackId);
      alert('Feedback deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting message feedback:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to delete message feedback. ';
      if (error.code === 'permission-denied') {
        errorMessage += 'Permission denied - make sure you are an admin and Firestore security rules allow deletion.';
      } else if (error.code === 'not-found') {
        errorMessage += 'Feedback not found - it may have already been deleted.';
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
    }
  };

  // Delete reported message
  const deleteReportedMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this reported message?')) {
      return;
    }
    
    try {
      console.log('🗑️ Attempting to delete reported message with ID:', messageId);
      const messageRef = doc(db, 'reportedMessages', messageId);
      await deleteDoc(messageRef);
      setReportedMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
      console.log('✅ Reported message deleted successfully:', messageId);
      alert('Reported message deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting reported message:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to delete reported message. ';
      if (error.code === 'permission-denied') {
        errorMessage += 'Permission denied - make sure you are an admin and Firestore security rules allow deletion.';
      } else if (error.code === 'not-found') {
        errorMessage += 'Message not found - it may have already been deleted.';
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
    }
  };

  // View as user (impersonation)
  const viewAsUser = (selectedUser) => {
    if (!selectedUser) return;
    
    // Store impersonation data in localStorage
    const impersonationData = {
      adminUid: user.uid,
      adminEmail: user.email,
      targetUid: selectedUser.id,
      targetEmail: selectedUser.email,
      targetDisplayName: selectedUser.displayName,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('fidora_impersonation', JSON.stringify(impersonationData));
    
    console.log('👤 Viewing as user:', selectedUser.email);
    alert(`Now viewing as: ${getSafeDisplayName(selectedUser.displayName) || selectedUser.email}\n\nYou will be redirected to the main chat. Look for the yellow banner at the top to stop impersonating.`);
    
    // Redirect to chat (root path)
    window.location.href = '/';
  };

  // Stop impersonation
  const stopImpersonation = () => {
    localStorage.removeItem('fidora_impersonation');
    console.log('👤 Stopped impersonation');
    window.location.href = '/admin';
  };

  const loadIssues = async () => {
    try {
      const issuesSnapshot = await getDocs(query(
        collection(db, 'issues'),
        orderBy('timestamp', 'desc'),
        limit(50)
      ));
      const issuesData = issuesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setIssues(issuesData);
    } catch (error) {
      console.error('Error loading issues:', error);
    }
  };

  const loadSupportTickets = async () => {
    try {
      // Load from supportTickets collection (new format)
      const ticketsSnapshot = await getDocs(query(
        collection(db, 'supportTickets'),
        orderBy('createdAt', 'desc'),
        limit(100)
      ));
      
      const ticketsData = ticketsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setSupportTickets(ticketsData);
      console.log(`✅ Loaded ${ticketsData.length} support tickets`);
    } catch (error) {
      console.error('Error loading support tickets:', error);
      // Try old format if new format fails
      try {
        const supportSnapshot = await getDocs(query(
          collection(db, 'support'),
          orderBy('timestamp', 'desc'),
          limit(100)
        ));
        const supportData = supportSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSupportTickets(supportData);
      } catch (err) {
        console.error('Error loading support tickets (fallback):', err);
        setSupportTickets([]);
      }
    }
  };

  const loadUserUpdates = async () => {
    try {
      // Get recent user activity/updates from Firestore
      // Try with orderBy first, fall back to unordered if index doesn't exist
      let usersSnapshot;
      try {
        usersSnapshot = await getDocs(query(
          collection(db, 'users'),
          orderBy('lastUpdated', 'desc'),
          limit(50)
        ));
      } catch (indexError) {
        console.warn('lastUpdated index not found, loading without ordering:', indexError.message);
        // Fallback: load without ordering
        usersSnapshot = await getDocs(query(
          collection(db, 'users'),
          limit(50)
        ));
      }
      
      const updates = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          email: data.email,
          displayName: data.displayName,
          subscriptionTier: data.subscriptionTier,
          coachingState: data.coachingState,
          usage: data.usage,
          lastUpdated: data.lastUpdated?.toDate?.() || new Date(),
          createdAt: data.createdAt?.toDate?.() || new Date()
        };
      });

      // Sort by lastUpdated in memory if we didn't get ordered results
      updates.sort((a, b) => b.lastUpdated - a.lastUpdated);

      setUserUpdates(updates);
      console.log(`✅ Loaded ${updates.length} user updates`);
    } catch (error) {
      console.error('Error loading user updates:', error);
      setUserUpdates([]);
    }
  };

  // Admin Actions
  const createPromoCode = async (code, tier, description, usageLimit = null) => {
    try {
      await addDoc(collection(db, 'promoCodes'), {
        code: code.toUpperCase(),
        tier,
        description,
        usageLimit,
        usedCount: 0,
        active: true,
        createdAt: new Date().toISOString(),
        createdBy: user.uid,
      });
      await loadPromoCodes();
      alert('✅ Promo code created!');
    } catch (error) {
      console.error('Error creating promo code:', error);
      alert('❌ Error creating promo code');
    }
  };

  const deletePromoCode = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await deleteDoc(doc(db, 'promoCodes', promoId));
      await loadPromoCodes();
      alert('✅ Promo code deleted!');
    } catch (error) {
      console.error('Error deleting promo code:', error);
      alert('❌ Error deleting promo code');
    }
  };

  const togglePromoCode = async (promoId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'promoCodes', promoId), {
        active: !currentStatus,
      });
      await loadPromoCodes();
    } catch (error) {
      console.error('Error toggling promo code:', error);
    }
  };

  const updateUserTier = async (userId, newTier) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        subscriptionTier: newTier,
        adminModified: true,
        adminModifiedAt: new Date().toISOString(),
        adminModifiedBy: user.uid,
      });
      await loadUsers();
      alert(`✅ User tier updated to ${newTier}`);
    } catch (error) {
      console.error('Error updating user tier:', error);
      alert('❌ Error updating user');
    }
  };

  const resetUserUsage = async (userId) => {
    if (!window.confirm('Reset usage for this user?')) return;
    try {
      await updateDoc(doc(db, 'users', userId), {
        'usage.textTokensUsed': 0,
        'usage.voiceCharactersUsed': 0,
        'usage.conversationCount': 0,
        'usage.lastResetDate': new Date().toISOString(),
      });
      await loadUsers();
      alert('✅ Usage reset!');
    } catch (error) {
      console.error('Error resetting usage:', error);
      alert('❌ Error resetting usage');
    }
  };

  const markIssueResolved = async (issueId) => {
    try {
      await updateDoc(doc(db, 'issues', issueId), {
        resolved: true,
        resolvedAt: new Date().toISOString(),
        resolvedBy: user.uid,
      });
      await loadIssues();
    } catch (error) {
      console.error('Error marking issue resolved:', error);
    }
  };

  const deleteUser = async (userId, userEmail) => {
    if (!window.confirm(`⚠️ Are you sure you want to DELETE user ${userEmail}?\n\nThis will permanently delete:\n- User profile\n- All conversations\n- All user data\n\nThis action CANNOT be undone!`)) {
      return;
    }
    
    try {
      // Delete user document from Firestore
      await deleteDoc(doc(db, 'users', userId));
      
      // Also try to delete their conversations
      try {
        const conversationsSnapshot = await getDocs(query(
          collection(db, 'conversations'),
          where('userId', '==', userId)
        ));
        const deletePromises = conversationsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
      } catch (convError) {
        console.warn('Error deleting conversations:', convError);
      }
      
      await loadUsers();
      alert('✅ User deleted successfully!');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('❌ Error deleting user: ' + error.message);
    }
  };

  const markSupportTicketResolved = async (ticketId) => {
    try {
      // Try new collection first (supportTickets)
      try {
        await updateDoc(doc(db, 'supportTickets', ticketId), {
          status: 'resolved',
          resolvedAt: new Date(),
          resolvedBy: user.uid,
        });
      } catch (e) {
        // Fallback to old collection (support)
        await updateDoc(doc(db, 'support', ticketId), {
          status: 'resolved',
          resolvedAt: new Date().toISOString(),
          resolvedBy: user.uid,
        });
      }
      await loadSupportTickets();
    } catch (error) {
      console.error('Error marking support ticket resolved:', error);
      alert('Error marking ticket resolved: ' + error.message);
    }
  };

  if (checkingAdmin) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        color: darkMode ? '#94a3b8' : '#64748b',
      }}>
        <div className="spinner" style={{ margin: '20px auto' }}>Checking admin access...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        color: darkMode ? '#94a3b8' : '#64748b',
      }}>
        <h1>🔒 Admin Access Only</h1>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        color: darkMode ? '#94a3b8' : '#64748b',
      }}>
        <div className="spinner">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '0 32px 60px',
      paddingTop: '140px',
      maxWidth: 1600,
      margin: '0 auto',
      minHeight: 'calc(100vh - 140px)',
      background: darkMode ? '#0f172a' : '#f8fafc',
    }}>
      {/* Admin Badge with View As User Button */}
      <div style={{
        marginBottom: 24,
        padding: 16,
        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
        borderRadius: 12,
        color: 'white',
        textAlign: 'center',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>
              🛡️ Admin Dashboard
            </h2>
            <p style={{ margin: '8px 0 0', opacity: 0.9 }}>
              Logged in as: {user.email}
            </p>
          </div>
          
          {/* View As User Button */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowViewAsDropdown(!showViewAsDropdown)}
              style={{
                padding: '10px 20px',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              View As User
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>

            {/* Dropdown */}
            {showViewAsDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 8,
                background: darkMode ? '#1e293b' : 'white',
                border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
                borderRadius: 8,
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                minWidth: 300,
                maxWidth: 400,
                maxHeight: 400,
                overflowY: 'auto',
                zIndex: 1000,
              }}>
                <div style={{
                  padding: 12,
                  borderBottom: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
                  fontWeight: 600,
                  fontSize: 14,
                  color: darkMode ? '#e2e8f0' : '#1f2937'
                }}>
                  Select a user to view as:
                </div>
                {users.map((u) => (
                  <div
                    key={u.id}
                    onClick={() => {
                      viewAsUser(u);
                      setShowViewAsDropdown(false);
                    }}
                    style={{
                      padding: 12,
                      cursor: 'pointer',
                      borderBottom: `1px solid ${darkMode ? '#334155' : '#f3f4f6'}`,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = darkMode ? '#334155' : '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, color: darkMode ? '#e2e8f0' : '#1f2937' }}>
                      {getSafeDisplayName(u.displayName) || u.email}
                    </div>
                    <div style={{ fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b' }}>
                      {u.email}
                    </div>
                    <div style={{ fontSize: 11, color: darkMode ? '#6b7280' : '#9ca3af', marginTop: 4 }}>
                      Tier: <strong>{u.subscriptionTier || 'free'}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* TABS - AT THE TOP! */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 24,
        borderBottom: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        overflowX: 'auto',
        paddingBottom: 0,
      }}>
        {[
          { id: 'overview', label: 'Overview', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
          { id: 'users', label: 'Users', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
          { id: 'promo', label: 'Promo Codes', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg> },
          { id: 'feedback', label: 'Feedback', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
          { id: 'support', label: 'Support', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> },
          { id: 'reports', label: 'Reports', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg> },
          { id: 'updates', label: 'Updates', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg> },
          { id: 'issues', label: 'Issues', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 20px',
              background: activeTab === tab.id
                ? (darkMode ? '#6366f1' : '#6366f1')
                : 'transparent',
              color: activeTab === tab.id
                ? 'white'
                : (darkMode ? '#94a3b8' : '#64748b'),
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #6366f1' : 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 15,
              borderRadius: '8px 8px 0 0',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* System Status Section - ALWAYS VISIBLE */}
      {/* Stats Overview */}
      {activeTab === 'overview' && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 24,
            marginBottom: 32,
          }}>
        {[
          { label: 'Total Users', value: stats.totalUsers, icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, color: '#6366f1' },
          { label: 'Active Users (7d)', value: stats.activeUsers, icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>, color: '#10b981' },
          { label: 'Monthly Revenue', value: `$${stats.totalRevenue}`, icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>, color: '#f59e0b' },
          { label: 'Storage Used', value: `${stats.storageUsed} MB`, icon: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>, color: '#8b5cf6' },
        ].map((stat, i) => (
          <div
            key={i}
            style={{
              padding: 24,
              background: darkMode ? '#1e293b' : 'white',
              borderRadius: 12,
              border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
            }}
          >
            <div style={{ marginBottom: 12 }}>{stat.icon}</div>
            <div style={{
              fontSize: 32,
              fontWeight: 700,
              color: stat.color,
              marginBottom: 8,
            }}>
              {stat.value}
            </div>
            <div style={{
              fontSize: 14,
              color: darkMode ? '#94a3b8' : '#64748b',
            }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>
        </>
      )}

      {/* Tab Content */}
      <div style={{ width: '100%', overflowY: 'auto' }}>
        {activeTab === 'users' && <UsersTab users={users} updateUserTier={updateUserTier} resetUserUsage={resetUserUsage} deleteUser={deleteUser} darkMode={darkMode} />}
        {activeTab === 'promo' && <PromoCodesTab promoCodes={promoCodes} createPromoCode={createPromoCode} deletePromoCode={deletePromoCode} togglePromoCode={togglePromoCode} darkMode={darkMode} />}
        {activeTab === 'feedback' && <FeedbackTab feedback={feedback} messageFeedback={messageFeedback} reportedMessages={reportedMessages} deleteMessageFeedback={deleteMessageFeedback} deleteReportedMessage={deleteReportedMessage} darkMode={darkMode} />}
        {activeTab === 'tickets' && <SupportTicketsTab supportTickets={supportTickets} markSupportTicketResolved={markSupportTicketResolved} darkMode={darkMode} />}
        {activeTab === 'reports' && <ReportsTab messageFeedback={messageFeedback} reportedMessages={reportedMessages} deleteMessageFeedback={deleteMessageFeedback} deleteReportedMessage={deleteReportedMessage} darkMode={darkMode} />}
        {activeTab === 'updates' && <UserUpdatesTab userUpdates={userUpdates} systemStatus={systemStatus} systemDetails={systemDetails} autoReconnect={autoReconnect} setAutoReconnect={setAutoReconnect} forceReconnect={forceReconnect} checkSystemStatus={checkSystemStatus} darkMode={darkMode} />}
        {activeTab === 'issues' && <IssuesTab issues={issues} markIssueResolved={markIssueResolved} darkMode={darkMode} />}
        {activeTab === 'support' && <SupportTab supportTickets={supportTickets} markSupportTicketResolved={markSupportTicketResolved} darkMode={darkMode} />}
      </div>
    </div>
  );
}

// Users Tab Component
function UsersTab({ users, updateUserTier, resetUserUsage, deleteUser, darkMode }) {
  const [search, setSearch] = useState('');

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.displayName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Search */}
      <input
        type="text"
        placeholder="Search users by email or name..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          padding: 16,
          marginBottom: 24,
          borderRadius: 8,
          border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
          background: darkMode ? '#1e293b' : 'white',
          color: darkMode ? 'white' : '#0f172a',
          fontSize: 16,
        }}
      />

      {/* Users Table */}
      <div style={{
        background: darkMode ? '#1e293b' : 'white',
        borderRadius: 12,
        border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: darkMode ? '#0f172a' : '#f8fafc' }}>
              <th style={tableHeaderStyle}>Email</th>
              <th style={tableHeaderStyle}>Name</th>
              <th style={tableHeaderStyle}>Tier</th>
              <th style={tableHeaderStyle}>Usage</th>
              <th style={tableHeaderStyle}>Created</th>
              <th style={tableHeaderStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} style={{ borderBottom: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
                <td style={tableCellStyle}>{user.email}</td>
                <td style={tableCellStyle}>{getSafeDisplayName(user.displayName) || 'N/A'}</td>
                <td style={tableCellStyle}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                    background: getTierColor(user.subscriptionTier),
                    color: 'white',
                  }}>
                    {user.subscriptionTier?.toUpperCase() || 'FREE'}
                  </span>
                </td>
                <td style={tableCellStyle}>
                  {user.usage?.conversationCount || 0} conversations
                </td>
                <td style={tableCellStyle}>
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </td>
                <td style={{ ...tableCellStyle, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <select
                    onChange={(e) => updateUserTier(user.id, e.target.value)}
                    value={user.subscriptionTier || 'free'}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
                      background: darkMode ? '#0f172a' : 'white',
                      color: darkMode ? 'white' : '#0f172a',
                      fontSize: 12,
                    }}
                  >
                    <option value="free">Free</option>
                    <option value="advisor">Advisor</option>
                    <option value="coach">Coach</option>
                    <option value="mentor">Mentor</option>
                  </select>
                  <button
                    onClick={() => resetUserUsage(user.id)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 4,
                      border: 'none',
                      background: '#f59e0b',
                      color: 'white',
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    Reset Usage
                  </button>
                  <button
                    onClick={() => deleteUser(user.id, user.email)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 4,
                      border: 'none',
                      background: '#ef4444',
                      color: 'white',
                      fontSize: 12,
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    🗑️ Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Promo Codes Tab Component
function PromoCodesTab({ promoCodes, createPromoCode, deletePromoCode, togglePromoCode, darkMode }) {
  const [showForm, setShowForm] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', tier: 'coach', description: '', usageLimit: '' });

  const handleCreate = () => {
    createPromoCode(
      newCode.code,
      newCode.tier,
      newCode.description,
      newCode.usageLimit ? parseInt(newCode.usageLimit) : null
    );
    setNewCode({ code: '', tier: 'coach', description: '', usageLimit: '' });
    setShowForm(false);
  };

  return (
    <div>
      <button
        onClick={() => setShowForm(!showForm)}
        style={{
          padding: '12px 24px',
          marginBottom: 24,
          background: '#10b981',
          color: 'white',
          border: 'none',
          borderRadius: 8,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {showForm ? '❌ Cancel' : '+ Create New Promo Code'}
      </button>

      {showForm && (
        <div style={{
          padding: 24,
          marginBottom: 24,
          background: darkMode ? '#1e293b' : 'white',
          borderRadius: 12,
          border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        }}>
          <h3 style={{ marginTop: 0 }}>Create New Promo Code</h3>
          <div style={{ display: 'grid', gap: 16 }}>
            <input
              placeholder="Code (e.g., SUMMER50)"
              value={newCode.code}
              onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
              style={inputStyle(darkMode)}
            />
            <select
              value={newCode.tier}
              onChange={(e) => setNewCode({ ...newCode, tier: e.target.value })}
              style={inputStyle(darkMode)}
            >
              <option value="advisor">Advisor</option>
              <option value="coach">Coach</option>
              <option value="mentor">Mentor</option>
            </select>
            <input
              placeholder="Description (e.g., 50% off for 6 months)"
              value={newCode.description}
              onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
              style={inputStyle(darkMode)}
            />
            <input
              type="number"
              placeholder="Usage Limit (leave empty for unlimited)"
              value={newCode.usageLimit}
              onChange={(e) => setNewCode({ ...newCode, usageLimit: e.target.value })}
              style={inputStyle(darkMode)}
            />
            <button
              onClick={handleCreate}
              style={{
                padding: 12,
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Create Promo Code
            </button>
          </div>
        </div>
      )}

      {/* Promo Codes List */}
      <div style={{
        background: darkMode ? '#1e293b' : 'white',
        borderRadius: 12,
        border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: darkMode ? '#0f172a' : '#f8fafc' }}>
              <th style={tableHeaderStyle}>Code</th>
              <th style={tableHeaderStyle}>Tier</th>
              <th style={tableHeaderStyle}>Description</th>
              <th style={tableHeaderStyle}>Used</th>
              <th style={tableHeaderStyle}>Status</th>
              <th style={tableHeaderStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {promoCodes.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ ...tableCellStyle, textAlign: 'center', padding: 40 }}>
                  No promo codes yet. Create your first one!
                </td>
              </tr>
            ) : (
              promoCodes.map((promo) => (
                <tr key={promo.id} style={{ borderBottom: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
                  <td style={{ ...tableCellStyle, fontWeight: 700, fontSize: 16 }}>{promo.code}</td>
                  <td style={tableCellStyle}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 600,
                      background: getTierColor(promo.tier),
                      color: 'white',
                    }}>
                      {promo.tier?.toUpperCase()}
                    </span>
                  </td>
                  <td style={tableCellStyle}>{promo.description}</td>
                  <td style={tableCellStyle}>
                    {promo.usedCount || 0}
                    {promo.usageLimit && ` / ${promo.usageLimit}`}
                  </td>
                  <td style={tableCellStyle}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={promo.active}
                        onChange={() => togglePromoCode(promo.id, promo.active)}
                        style={{ cursor: 'pointer' }}
                      />
                      {promo.active ? '✅ Active' : '❌ Inactive'}
                    </label>
                  </td>
                  <td style={tableCellStyle}>
                    <button
                      onClick={() => deletePromoCode(promo.id)}
                      style={{
                        padding: '4px 12px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Feedback Tab Component
function FeedbackTab({ feedback, messageFeedback, reportedMessages, deleteMessageFeedback, deleteReportedMessage, darkMode }) {
  const [activeSubTab, setActiveSubTab] = useState('messages'); // 'messages', 'general', or 'reported'
  const [groupByUser, setGroupByUser] = useState(true); // Group by user by default
  const [sortBy, setSortBy] = useState('count'); // 'count', 'date', 'user'

  // Group feedback by user
  const groupFeedbackByUser = (feedbackArray) => {
    const grouped = {};
    
    feedbackArray.forEach(item => {
      const userKey = item.userEmail || item.userName || item.userId || 'Anonymous';
      
      if (!grouped[userKey]) {
        grouped[userKey] = {
          userEmail: item.userEmail,
          userName: item.userName,
          userId: item.userId,
          items: []
        };
      }
      
      grouped[userKey].items.push(item);
    });

    // Convert to array and sort by count (high to low)
    const groupedArray = Object.entries(grouped).map(([key, value]) => ({
      userKey: key,
      ...value,
      count: value.items.length
    }));

    // Sort based on sortBy state
    if (sortBy === 'count') {
      groupedArray.sort((a, b) => b.count - a.count);
    } else if (sortBy === 'user') {
      groupedArray.sort((a, b) => a.userKey.localeCompare(b.userKey));
    } else if (sortBy === 'date') {
      groupedArray.sort((a, b) => {
        const dateA = a.items[0]?.timestamp?.toDate?.() || new Date(0);
        const dateB = b.items[0]?.timestamp?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
    }

    return groupedArray;
  };

  // Group reported messages by user
  const groupReportedByUser = (reportedArray) => {
    const grouped = {};
    
    reportedArray.forEach(item => {
      const userKey = item.reportedBy || 'Anonymous';
      
      if (!grouped[userKey]) {
        grouped[userKey] = {
          reportedBy: item.reportedBy,
          items: []
        };
      }
      
      grouped[userKey].items.push(item);
    });

    // Convert to array and sort by count (high to low)
    const groupedArray = Object.entries(grouped).map(([key, value]) => ({
      userKey: key,
      ...value,
      count: value.items.length
    }));

    // Sort based on sortBy state
    if (sortBy === 'count') {
      groupedArray.sort((a, b) => b.count - a.count);
    } else if (sortBy === 'user') {
      groupedArray.sort((a, b) => a.userKey.localeCompare(b.userKey));
    } else if (sortBy === 'date') {
      groupedArray.sort((a, b) => {
        const dateA = a.items[0]?.reportedAt?.toDate?.() || new Date(0);
        const dateB = b.items[0]?.reportedAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
    }

    return groupedArray;
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: darkMode ? '#94a3b8' : '#64748b' }}>
            <input
              type="checkbox"
              checked={groupByUser}
              onChange={(e) => setGroupByUser(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Group by User
          </label>
          
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: darkMode ? '#94a3b8' : '#64748b' }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
                background: darkMode ? '#1e293b' : 'white',
                color: darkMode ? '#e2e8f0' : '#1e293b',
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              <option value="count">Count (High to Low)</option>
              <option value="date">Most Recent</option>
              <option value="user">User (A-Z)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, borderBottom: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
        <button
          onClick={() => setActiveSubTab('messages')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeSubTab === 'messages' ? `3px solid #818cf8` : '3px solid transparent',
            color: activeSubTab === 'messages' ? '#818cf8' : (darkMode ? '#94a3b8' : '#64748b'),
            fontWeight: activeSubTab === 'messages' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14,
            marginBottom: -2
          }}
        >
          👍👎 Message Feedback ({messageFeedback?.length || 0})
        </button>
        <button
          onClick={() => setActiveSubTab('reported')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeSubTab === 'reported' ? `3px solid #818cf8` : '3px solid transparent',
            color: activeSubTab === 'reported' ? '#818cf8' : (darkMode ? '#94a3b8' : '#64748b'),
            fontWeight: activeSubTab === 'reported' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14,
            marginBottom: -2
          }}
        >
          ⚠️ Reported Messages ({reportedMessages?.length || 0})
        </button>
        <button
          onClick={() => setActiveSubTab('general')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeSubTab === 'general' ? `3px solid #818cf8` : '3px solid transparent',
            color: activeSubTab === 'general' ? '#818cf8' : (darkMode ? '#94a3b8' : '#64748b'),
            fontWeight: activeSubTab === 'general' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14,
            marginBottom: -2
          }}
        >
          ⭐ General Feedback ({feedback?.length || 0})
        </button>
      </div>

      {/* Message Feedback (Thumbs Up/Down) */}
      {activeSubTab === 'messages' && (
        <div style={{ display: 'grid', gap: 16 }}>
          {!messageFeedback || messageFeedback.length === 0 ? (
            <div style={{
              padding: 40,
              textAlign: 'center',
              background: darkMode ? '#1e293b' : 'white',
              borderRadius: 12,
              color: darkMode ? '#94a3b8' : '#64748b',
            }}>
              No message feedback yet
            </div>
          ) : groupByUser ? (
            // GROUPED BY USER VIEW
            groupFeedbackByUser(messageFeedback).map((userGroup) => (
              <div
                key={userGroup.userKey}
                style={{
                  padding: 20,
                  background: darkMode ? '#1e293b' : 'white',
                  borderRadius: 12,
                  border: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
                }}
              >
                {/* User Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                  paddingBottom: 12,
                  borderBottom: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`
                }}>
                  <div>
                    <strong style={{ fontSize: 16, color: darkMode ? '#e2e8f0' : '#1e293b' }}>
                      👤 {userGroup.userName || userGroup.userEmail || userGroup.userKey}
                    </strong>
                    <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b', marginTop: 4 }}>
                      {userGroup.count} feedback message{userGroup.count !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 24 }}>
                    {userGroup.items.filter(i => i.feedbackType === 'up').length} 👍 / {userGroup.items.filter(i => i.feedbackType === 'down').length} 👎
                  </div>
                </div>

                {/* Individual Feedback Items */}
                <div style={{ display: 'grid', gap: 12 }}>
                  {userGroup.items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        padding: 12,
                        background: darkMode ? '#0f172a' : '#f8fafc',
                        borderRadius: 8,
                        borderLeft: `4px solid ${item.feedbackType === 'down' ? '#ef4444' : '#10b981'}`,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'start' }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                          <div style={{ fontSize: 24 }}>
                            {item.feedbackType === 'up' ? '👍' : '👎'}
                          </div>
                          <div style={{ fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b' }}>
                            {item.timestamp?.toDate?.().toLocaleString() || 'Recently'}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteMessageFeedback(item.id)}
                          style={{
                            padding: '4px 10px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 10,
                            fontWeight: 600
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                      <div style={{
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: darkMode ? '#cbd5e1' : '#475569',
                      }}>
                        {item.messageContent}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            // UNGROUPED VIEW (Original)
            messageFeedback.map((item) => (
              <div
                key={item.id}
                style={{
                  padding: 20,
                  background: darkMode ? '#1e293b' : 'white',
                  borderRadius: 12,
                  border: `2px solid ${item.feedbackType === 'down' ? '#ef4444' : '#10b981'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'start' }}>
                  <div>
                    <strong>{item.userName || item.userEmail || 'Anonymous'}</strong>
                    <div style={{ fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b' }}>
                      {item.timestamp?.toDate?.().toLocaleString() || 'Recently'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      onClick={() => deleteMessageFeedback(item.id)}
                      style={{
                        padding: '6px 12px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 11,
                        fontWeight: 600
                      }}
                    >
                      🗑️ Delete
                    </button>
                    <div style={{ fontSize: 32 }}>
                      {item.feedbackType === 'up' ? '👍' : '👎'}
                    </div>
                  </div>
                </div>
                <div style={{
                  padding: 16,
                  background: darkMode ? '#0f172a' : '#f8fafc',
                  borderRadius: 8,
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: darkMode ? '#e2e8f0' : '#1e293b',
                  maxHeight: 200,
                  overflow: 'auto'
                }}>
                  {item.messageContent}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Reported Messages */}
      {activeSubTab === 'reported' && (
        <div style={{ display: 'grid', gap: 16 }}>
          {!reportedMessages || reportedMessages.length === 0 ? (
            <div style={{
              padding: 40,
              textAlign: 'center',
              background: darkMode ? '#1e293b' : 'white',
              borderRadius: 12,
              color: darkMode ? '#94a3b8' : '#64748b',
            }}>
              No reported messages yet
            </div>
          ) : groupByUser ? (
            // GROUPED BY USER VIEW
            groupReportedByUser(reportedMessages).map((userGroup) => (
              <div
                key={userGroup.userKey}
                style={{
                  padding: 20,
                  background: darkMode ? '#1e293b' : 'white',
                  borderRadius: 12,
                  border: `2px solid #ef4444`,
                }}
              >
                {/* User Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                  paddingBottom: 12,
                  borderBottom: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`
                }}>
                  <div>
                    <strong style={{ fontSize: 16, color: darkMode ? '#e2e8f0' : '#1e293b' }}>
                      👤 Reported by: {userGroup.userKey}
                    </strong>
                    <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b', marginTop: 4 }}>
                      {userGroup.count} reported message{userGroup.count !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 32, color: '#ef4444' }}>
                    ⚠️ {userGroup.count}
                  </div>
                </div>

                {/* Individual Reported Items */}
                <div style={{ display: 'grid', gap: 12 }}>
                  {userGroup.items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        padding: 12,
                        background: darkMode ? '#0f172a' : '#fef2f2',
                        borderRadius: 8,
                        borderLeft: `4px solid #ef4444`,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'start' }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                          <div style={{ fontSize: 20, color: '#ef4444' }}>
                            ⚠️
                          </div>
                          <div style={{ fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b' }}>
                            {item.reportedAt?.toDate?.().toLocaleString() || 'Recently'}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteReportedMessage(item.id)}
                          style={{
                            padding: '4px 10px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 10,
                            fontWeight: 600
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                      <div style={{
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: darkMode ? '#cbd5e1' : '#475569',
                      }}>
                        {item.messageContent}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            // UNGROUPED VIEW (Original)
            reportedMessages.map((item) => (
              <div
                key={item.id}
                style={{
                  padding: 20,
                  background: darkMode ? '#1e293b' : 'white',
                  borderRadius: 12,
                  border: `2px solid #ef4444`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'start' }}>
                  <div>
                    <strong>Reported by: {item.reportedBy || 'Anonymous'}</strong>
                    <div style={{ fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b' }}>
                      {item.reportedAt?.toDate?.().toLocaleString() || 'Recently'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      onClick={() => deleteReportedMessage(item.id)}
                      style={{
                        padding: '6px 12px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 11,
                        fontWeight: 600
                      }}
                    >
                      🗑️ Delete
                    </button>
                    <div style={{ fontSize: 24, color: '#ef4444' }}>
                      ⚠️
                    </div>
                  </div>
                </div>
                <div style={{
                  padding: 16,
                  background: darkMode ? '#0f172a' : '#fef2f2',
                  borderRadius: 8,
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: darkMode ? '#e2e8f0' : '#1e293b',
                  maxHeight: 200,
                  overflow: 'auto'
                }}>
                  {item.messageContent}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* General Feedback (Star Ratings) */}
      {activeSubTab === 'general' && (
        <div style={{ display: 'grid', gap: 16 }}>
          {!feedback || feedback.length === 0 ? (
            <div style={{
              padding: 40,
              textAlign: 'center',
              background: darkMode ? '#1e293b' : 'white',
              borderRadius: 12,
              color: darkMode ? '#94a3b8' : '#64748b',
            }}>
              No general feedback yet
            </div>
          ) : (
            feedback.map((item) => (
              <div
                key={item.id}
                style={{
                  padding: 20,
                  background: darkMode ? '#1e293b' : 'white',
                  borderRadius: 12,
                  border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <strong>{item.userName || item.userEmail || 'Anonymous'}</strong>
                    <div style={{ fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b' }}>
                      {item.timestamp?.toDate?.().toLocaleString() || 'Recently'}
                    </div>
                  </div>
                  <div>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} style={{ color: i < (item.rating || 0) ? '#f59e0b' : '#cbd5e1', fontSize: 20 }}>
                        ★
                      </span>
                    ))}
                  </div>
                </div>
                {item.comment && (
                  <p style={{ margin: 0, lineHeight: 1.6, color: darkMode ? '#e2e8f0' : '#1e293b' }}>
                    {item.comment}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Issues Tab Component
function IssuesTab({ issues, markIssueResolved, darkMode }) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {issues.length === 0 ? (
        <div style={{
          padding: 40,
          textAlign: 'center',
          background: darkMode ? '#1e293b' : 'white',
          borderRadius: 12,
          color: darkMode ? '#94a3b8' : '#64748b',
        }}>
          No issues reported
        </div>
      ) : (
        issues.map((issue) => (
          <div
            key={issue.id}
            style={{
              padding: 20,
              background: darkMode ? '#1e293b' : 'white',
              borderRadius: 12,
              border: `2px solid ${issue.resolved ? '#10b981' : '#ef4444'}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <strong>{issue.userEmail || 'Anonymous'}</strong>
                <div style={{ fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b' }}>
                  {issue.timestamp?.toDate?.().toLocaleString() || 'Recently'}
                </div>
              </div>
              {!issue.resolved && (
                <button
                  onClick={() => markIssueResolved(issue.id)}
                  style={{
                    padding: '6px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  Mark Resolved
                </button>
              )}
              {issue.resolved && (
                <span style={{
                  padding: '6px 16px',
                  background: '#10b981',
                  color: 'white',
                  borderRadius: 6,
                  fontSize: 14,
                }}>
                  ✅ Resolved
                </span>
              )}
            </div>
            <h4 style={{ margin: '0 0 8px' }}>{issue.title || 'Issue Report'}</h4>
            <p style={{ margin: 0, lineHeight: 1.6 }}>{issue.description}</p>
            {issue.errorMessage && (
              <pre style={{
                marginTop: 12,
                padding: 12,
                background: darkMode ? '#0f172a' : '#f8fafc',
                borderRadius: 8,
                fontSize: 12,
                overflow: 'auto',
              }}>
                {issue.errorMessage}
              </pre>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// Support Tickets Tab Component - NEW
function SupportTicketsTab({ supportTickets, markSupportTicketResolved, darkMode }) {
  const [filter, setFilter] = useState('all');
  
  const filteredTickets = supportTickets.filter(ticket => {
    if (filter === 'all') return true;
    if (filter === 'open') return ticket.status === 'open';
    if (filter === 'resolved') return ticket.status === 'resolved';
    return ticket.type === filter;
  });

  const priorityColors = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444'
  };

  const typeIcons = {
    bug: '🐛',
    feature: '✨',
    error: '⚠️',
    feedback: '💬',
    other: '❓'
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <button onClick={() => setFilter('all')} style={{ padding: '8px 16px', borderRadius: 8, background: filter === 'all' ? '#6366f1' : (darkMode ? '#334155' : '#e2e8f0'), color: filter === 'all' ? 'white' : 'inherit', border: 'none', cursor: 'pointer' }}>
          All ({supportTickets.length})
        </button>
        <button onClick={() => setFilter('open')} style={{ padding: '8px 16px', borderRadius: 8, background: filter === 'open' ? '#6366f1' : (darkMode ? '#334155' : '#e2e8f0'), color: filter === 'open' ? 'white' : 'inherit', border: 'none', cursor: 'pointer' }}>
          Open ({supportTickets.filter(t => t.status === 'open').length})
        </button>
        <button onClick={() => setFilter('resolved')} style={{ padding: '8px 16px', borderRadius: 8, background: filter === 'resolved' ? '#6366f1' : (darkMode ? '#334155' : '#e2e8f0'), color: filter === 'resolved' ? 'white' : 'inherit', border: 'none', cursor: 'pointer' }}>
          Resolved ({supportTickets.filter(t => t.status === 'resolved').length})
        </button>
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {filteredTickets.map((ticket) => (
          <div key={ticket.id} style={{
            padding: 20,
            background: darkMode ? '#1e293b' : 'white',
            borderRadius: 12,
            border: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ fontSize: 24 }}>{typeIcons[ticket.type] || '📝'}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>
                    {ticket.subject}
                  </div>
                  <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b' }}>
                    From: {ticket.userName || ticket.userEmail} • {ticket.createdAt?.toLocaleDateString?.() || 'N/A'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  background: ticket.status === 'open' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                  color: ticket.status === 'open' ? '#ef4444' : '#10b981'
                }}>
                  {ticket.status || 'open'}
                </span>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  background: `${priorityColors[ticket.priority]}20`,
                  color: priorityColors[ticket.priority]
                }}>
                  {ticket.priority || 'medium'}
                </span>
              </div>
            </div>
            
            <div style={{
              padding: 12,
              background: darkMode ? '#0f172a' : '#f8fafc',
              borderRadius: 8,
              marginBottom: 12,
              fontSize: 14,
              lineHeight: 1.6
            }}>
              {ticket.description}
            </div>

            {ticket.status !== 'resolved' && (
              <button
                onClick={() => markSupportTicketResolved(ticket.id)}
                style={{
                  padding: '8px 16px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14
                }}
              >
                Mark as Resolved
              </button>
            )}
          </div>
        ))}

        {filteredTickets.length === 0 && (
          <div style={{
            padding: 40,
            textAlign: 'center',
            color: darkMode ? '#94a3b8' : '#64748b',
            background: darkMode ? '#1e293b' : '#f8fafc',
            borderRadius: 12
          }}>
            No tickets found
          </div>
        )}
      </div>
    </div>
  );
}

// Reports Tab Component - NEW
function ReportsTab({ messageFeedback, reportedMessages, deleteMessageFeedback, deleteReportedMessage, darkMode }) {
  const [activeView, setActiveView] = useState('feedback');

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button onClick={() => setActiveView('feedback')} style={{ padding: '8px 16px', borderRadius: 8, background: activeView === 'feedback' ? '#6366f1' : (darkMode ? '#334155' : '#e2e8f0'), color: activeView === 'feedback' ? 'white' : 'inherit', border: 'none', cursor: 'pointer' }}>
          👍 Message Feedback ({messageFeedback.length})
        </button>
        <button onClick={() => setActiveView('reported')} style={{ padding: '8px 16px', borderRadius: 8, background: activeView === 'reported' ? '#6366f1' : (darkMode ? '#334155' : '#e2e8f0'), color: activeView === 'reported' ? 'white' : 'inherit', border: 'none', cursor: 'pointer' }}>
          ⚠️ Reported Messages ({reportedMessages.length})
        </button>
      </div>

      {activeView === 'feedback' && (
        <div style={{ display: 'grid', gap: 16 }}>
          {messageFeedback.map((fb, i) => (
            <div key={i} style={{
              padding: 20,
              background: darkMode ? '#1e293b' : 'white',
              borderRadius: 12,
              border: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'start' }}>
                <span style={{ fontSize: 32 }}>{fb.feedbackType === 'up' ? '👍' : '👎'}</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                  <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b', textAlign: 'right' }}>
                    {fb.userName || fb.userEmail}<br/>
                    {fb.timestamp?.toDate?.()?.toLocaleString?.() || 'N/A'}
                  </div>
                  <button
                    onClick={() => deleteMessageFeedback(fb.id)}
                    style={{
                      padding: '6px 12px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 600
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
              <div style={{
                padding: 12,
                background: darkMode ? '#0f172a' : '#f8fafc',
                borderRadius: 8,
                fontSize: 14,
                lineHeight: 1.6
              }}>
                {fb.messageContent}
              </div>
            </div>
          ))}
          {messageFeedback.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: darkMode ? '#94a3b8' : '#64748b' }}>
              No feedback yet
            </div>
          )}
        </div>
      )}

      {activeView === 'reported' && (
        <div style={{ display: 'grid', gap: 16 }}>
          {reportedMessages.map((msg, i) => (
            <div key={i} style={{
              padding: 20,
              background: darkMode ? '#1e293b' : 'white',
              borderRadius: 12,
              border: `2px solid #ef4444`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'start' }}>
                <span style={{ fontSize: 32, color: '#ef4444' }}>⚠️</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                  <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b', textAlign: 'right' }}>
                    Reported by: {msg.reportedBy}<br/>
                    {msg.reportedAt?.toDate?.()?.toLocaleString?.() || 'N/A'}
                  </div>
                  <button
                    onClick={() => deleteReportedMessage(msg.id)}
                    style={{
                      padding: '6px 12px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 600
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
              <div style={{
                padding: 12,
                background: darkMode ? '#0f172a' : '#f8fafc',
                borderRadius: 8,
                fontSize: 14,
                lineHeight: 1.6
              }}>
                {msg.messageContent}
              </div>
            </div>
          ))}
          {reportedMessages.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: darkMode ? '#94a3b8' : '#64748b' }}>
              No reported messages
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// User Updates Tab Component - NOW WITH DETAILED SYSTEM INFO
function UserUpdatesTab({ userUpdates, systemStatus, systemDetails, autoReconnect, setAutoReconnect, forceReconnect, checkSystemStatus, darkMode }) {
  const [showDetails, setShowDetails] = useState({
    collections: false,
    functions: false,
    agents: false
  });

  // Live flow + connection diagnostics
  const [flowDiag, setFlowDiag] = React.useState({
    apiHealth: null,
    mcpLatencyMs: null,
    chatLatencyMs: null,
    lastRun: null,
    error: null
  });

  async function runLiveFlowTrace() {
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'fidora-72b3d';
    const region = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'us-central1';
    const base = `https://${region}-${projectId}.cloudfunctions.net`;
    const results = { apiHealth: null, mcpLatencyMs: null, chatLatencyMs: null, error: null };
    try {
      // API health
      const healthResp = await fetch(`${base}/health`, { method: 'GET' });
      results.apiHealth = healthResp.ok ? 'ok' : `error ${healthResp.status}`;

      // MCP multi-agent synthesis (Firebase-native)
      const m0 = performance.now();
      const mcpResp = await fetch(`${base}/api/multi-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentTypes: ['career','resume'], query: 'Quick synthesis check', context: { source: 'admin-diagnostics' } })
      });
      const m1 = performance.now();
      results.mcpLatencyMs = mcpResp.ok ? Math.round(m1 - m0) : null;
      if (!mcpResp.ok) throw new Error(`MCP error ${mcpResp.status}`);

      // Chat HTTP minimal call (unauth okay)
      const c0 = performance.now();
      const chatResp = await fetch(`${base}/fidoraChatHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: 'Health check: say OK', history: [] })
      });
      const c1 = performance.now();
      results.chatLatencyMs = chatResp.ok ? Math.round(c1 - c0) : null;
      if (!chatResp.ok) throw new Error(`Chat error ${chatResp.status}`);

      setFlowDiag({ ...results, lastRun: new Date() });
    } catch (e) {
      setFlowDiag(prev => ({ ...prev, ...results, lastRun: new Date(), error: e.message || String(e) }));
    }
  }

  return (
    <div>
      {/* System Status - Live Connections */}
      <div style={{
        padding: 24,
        background: darkMode ? '#1e293b' : 'white',
        borderRadius: 12,
        border: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        marginBottom: 32
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v6m0 6v6m-7-7h6m6 0h6"/>
              <circle cx="12" cy="12" r="10"/>
            </svg>
            System Status - Live Connections
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: darkMode ? '#cbd5e1' : '#64748b', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={autoReconnect} 
                onChange={(e) => setAutoReconnect(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Auto-Reconnect (30s)
            </label>
            <button
              onClick={forceReconnect}
              style={{
                padding: '8px 16px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
              title="Force reconnect all systems"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              Force Reconnect
            </button>
            <button
              onClick={() => checkSystemStatus()}
              style={{
                padding: '8px 16px',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              Refresh Status
            </button>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {[
            { label: 'Firebase Firestore', key: 'firebase', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg> },
            { label: 'Cloud Functions', key: 'functions', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg> },
            { label: 'OpenAI GPT-4', key: 'openai', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg> },
            { label: '65-Block Coach Brain', key: 'blocks', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
            { label: 'Multi-Agent System', key: 'agents', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/><path d="M8 20v-2.5"/><path d="M16 20v-2.5"/><path d="M12 2v2.5"/><path d="M12 2v2.5"/><path d="M9 7v4"/><path d="M15 7v4"/><path d="M12 14v4"/></svg> },
          ].map((item) => {
            const status = systemStatus[item.key];
            const statusColors = {
              connected: '#10b981',
              checking: '#f59e0b',
              deploying: '#3b82f6',
              error: '#ef4444',
              unknown: '#6b7280'
            };
            const statusText = {
              connected: '✅ Connected',
              checking: '⏳ Checking...',
              deploying: '🚀 Deploying...',
              error: '❌ Error',
              unknown: '❓ Unknown'
            };
            return (
              <div key={item.key} style={{
                padding: 16,
                background: darkMode ? '#0f172a' : '#f8fafc',
                borderRadius: 8,
                border: `2px solid ${statusColors[status]}`,
              }}>
                <div style={{ marginBottom: 8, color: statusColors[status] }}>{item.icon}</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: darkMode ? '#e2e8f0' : '#1e293b' }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, color: statusColors[status] }}>
                  {statusText[status]}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>
        System Details
      </h3>

      {/* Detailed System Information - Expandable Sections */}
      <div style={{ display: 'grid', gap: 16, marginBottom: 32 }}>
        
        {/* Firestore Collections */}
        <div style={{
          padding: 20,
          background: darkMode ? '#1e293b' : 'white',
          borderRadius: 12,
          border: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`
        }}>
          <button
            onClick={() => setShowDetails({...showDetails, collections: !showDetails.collections})}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              color: darkMode ? '#e2e8f0' : '#1e293b'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 24 }}>🗄️</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Firestore Collections</div>
                <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b' }}>
                  {systemDetails.collections.length} collections · {systemDetails.totalDocs.toLocaleString()} total documents
                </div>
              </div>
            </div>
            <div style={{ fontSize: 24 }}>{showDetails.collections ? '▼' : '▶'}</div>
          </button>
          
          {showDetails.collections && (
            <div style={{ marginTop: 16, display: 'grid', gap: 8 }}>
              {systemDetails.collections.map(coll => (
                <div key={coll.name} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  background: darkMode ? '#0f172a' : '#f8fafc',
                  borderRadius: 6,
                  fontSize: 13
                }}>
                  <span style={{ fontFamily: 'monospace' }}>{coll.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ color: darkMode ? '#94a3b8' : '#64748b' }}>
                      {coll.count.toLocaleString()} docs
                    </span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      background: coll.status === 'active' ? 'rgba(34, 197, 94, 0.1)' : 
                                coll.status === 'empty' ? 'rgba(156, 163, 175, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      color: coll.status === 'active' ? '#22c55e' :
                            coll.status === 'empty' ? '#9ca3af' : '#ef4444'
                    }}>
                      {coll.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cloud Functions */}
        <div style={{
          padding: 20,
          background: darkMode ? '#1e293b' : 'white',
          borderRadius: 12,
          border: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`
        }}>
          <button
            onClick={() => setShowDetails({...showDetails, functions: !showDetails.functions})}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              color: darkMode ? '#e2e8f0' : '#1e293b'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 24 }}>⚡</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Cloud Functions</div>
                <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b' }}>
                  {systemDetails.cloudFunctions.length} serverless functions deployed
                </div>
              </div>
            </div>
            <div style={{ fontSize: 24 }}>{showDetails.functions ? '▼' : '▶'}</div>
          </button>
          
          {showDetails.functions && (
            <div style={{ marginTop: 16, display: 'grid', gap: 8 }}>
              {systemDetails.cloudFunctions.map(func => (
                <div key={func.name} style={{
                  padding: '10px 12px',
                  background: darkMode ? '#0f172a' : '#f8fafc',
                  borderRadius: 6,
                  fontSize: 13
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{func.name}</span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      background: 'rgba(34, 197, 94, 0.1)',
                      color: '#22c55e'
                    }}>
                      ✓ {func.status}
                    </span>
                  </div>
                  <div style={{ color: darkMode ? '#94a3b8' : '#64748b', fontSize: 12 }}>
                    {func.purpose}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      {/* MCP Server */}
      <div style={{
        padding: 20,
        background: darkMode ? '#1e293b' : 'white',
        borderRadius: 12,
        border: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, color: darkMode ? '#e2e8f0' : '#1e293b' }}>
          <div style={{ fontSize: 24 }}>🧠</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>MCP Server</div>
            <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b' }}>
              URL: <span style={{ color: darkMode ? '#e2e8f0' : '#1e293b' }}>{systemDetails.mcp?.url || 'not set'}</span>
            </div>
          </div>
          <span style={{
            marginLeft: 'auto',
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            background: systemDetails.mcp?.status === 'active' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
            color: systemDetails.mcp?.status === 'active' ? '#22c55e' : '#ef4444'
          }}>
            {systemDetails.mcp?.status || 'unknown'}
          </span>
        </div>
        {systemDetails.mcp?.info && (
          <div style={{
            padding: 12,
            background: darkMode ? '#0f172a' : '#f8fafc',
            borderRadius: 8,
            fontSize: 12,
            color: darkMode ? '#94a3b8' : '#64748b'
          }}>
            <div>version: {String(systemDetails.mcp.info.version || 'n/a')}</div>
            <div>agents: {Array.isArray(systemDetails.mcp.info.agents) ? systemDetails.mcp.info.agents.join(', ') : 'n/a'}</div>
          </div>
        )}
      </div>

        {/* Architecture & Live Flow */}
        <div style={{
          padding: 20,
          background: darkMode ? '#1e293b' : 'white',
          borderRadius: 12,
          border: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: darkMode ? '#e2e8f0' : '#1e293b' }}>Architecture & Live Flow</div>
            <button onClick={runLiveFlowTrace} style={{
              padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer'
            }}>Run Live Trace</button>
          </div>
          {/* Simple diagram */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', color: darkMode ? '#e2e8f0' : '#1e293b', marginBottom: 12 }}>
            <div style={{ padding: '8px 12px', borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc' }}>Web App</div>
            <div>→</div>
            <div style={{ padding: '8px 12px', borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc' }}>Cloud Functions API</div>
            <div>→</div>
            <div style={{ padding: '8px 12px', borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc' }}>MCP (multi-agent)</div>
            <div>→</div>
            <div style={{ padding: '8px 12px', borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc' }}>OpenAI</div>
            <div>↘</div>
            <div style={{ padding: '8px 12px', borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc' }}>Firestore / Storage</div>
          </div>
          {/* Live metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div style={{ padding: 12, borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc' }}>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>API Health</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{flowDiag.apiHealth || '—'}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc' }}>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>MCP Latency</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{flowDiag.mcpLatencyMs != null ? `${flowDiag.mcpLatencyMs} ms` : '—'}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc' }}>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Chat Latency</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{flowDiag.chatLatencyMs != null ? `${flowDiag.chatLatencyMs} ms` : '—'}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc' }}>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Last Run</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{flowDiag.lastRun ? new Date(flowDiag.lastRun).toLocaleTimeString() : '—'}</div>
            </div>
          </div>
          {flowDiag.error && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 13 }}>
              {flowDiag.error}
            </div>
          )}
        </div>

        {/* AI Agents */}
        <div style={{
          padding: 20,
          background: darkMode ? '#1e293b' : 'white',
          borderRadius: 12,
          border: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`
        }}>
          <button
            onClick={() => setShowDetails({...showDetails, agents: !showDetails.agents})}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              color: darkMode ? '#e2e8f0' : '#1e293b'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 24 }}>🤖</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>AI Career Coaches</div>
                <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b' }}>
                  {systemDetails.agents.length} coaches with unique personalities & voices
                </div>
              </div>
            </div>
            <div style={{ fontSize: 24 }}>{showDetails.agents ? '▼' : '▶'}</div>
          </button>
          
          {showDetails.agents && (
            <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
              {systemDetails.agents.map(agent => (
                <div key={agent.name} style={{
                  padding: '12px 14px',
                  background: darkMode ? '#0f172a' : '#f8fafc',
                  borderRadius: 6,
                  fontSize: 13
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 6 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{agent.name}</div>
                      <div style={{ color: darkMode ? '#94a3b8' : '#64748b', fontSize: 12 }}>
                        {agent.role}
                      </div>
                    </div>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      background: agent.tier === 'free' ? 'rgba(156, 163, 175, 0.1)' :
                                agent.tier === 'coach' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(139, 92, 246, 0.1)',
                      color: agent.tier === 'free' ? '#9ca3af' :
                            agent.tier === 'coach' ? '#6366f1' : '#8b5cf6'
                    }}>
                      {agent.tier.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: darkMode ? '#94a3b8' : '#64748b', fontFamily: 'monospace' }}>
                    🔊 Voice: {agent.voice}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* System Summary */}
        <div style={{
          padding: 16,
          background: darkMode ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)',
          borderRadius: 8,
          border: `1px solid ${darkMode ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.2)'}`
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: darkMode ? '#e2e8f0' : '#1e293b' }}>
            📊 System Summary:
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: darkMode ? '#cbd5e1' : '#64748b', lineHeight: 1.8 }}>
            <li>65-Block Coaching Framework (Interview, Resume, Career, Salary agents)</li>
            <li>Real-time multi-agent collaboration</li>
            <li>OpenAI GPT-4 Turbo + TTS</li>
            <li>Firebase Cloud Functions (Serverless)</li>
            <li>Career Memory Stack (Identity, Narrative, Coaching, Emotional, Engagement)</li>
            <li>Resume Parser & ATS Optimizer</li>
            <li>STAR Interview Analyzer</li>
            <li>Last checked: {systemDetails.lastChecked ? new Date(systemDetails.lastChecked).toLocaleString() : 'Never'}</li>
          </ul>
        </div>
      </div>

      <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>
        Recent User Activity
      </h3>
      <div style={{ display: 'grid', gap: 16 }}>
        {userUpdates.map((update) => (
          <div key={update.id} style={{
            padding: 20,
            background: darkMode ? '#1e293b' : 'white',
            borderRadius: 12,
            border: `2px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>
                  {getSafeDisplayName(update.displayName) || 'Anonymous'}
                </div>
                <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b' }}>
                  {update.email}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  padding: '4px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  background: update.subscriptionTier === 'mentor' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                  color: update.subscriptionTier === 'mentor' ? '#8b5cf6' : '#6366f1',
                  display: 'inline-block',
                  marginBottom: 4
                }}>
                  {update.subscriptionTier?.toUpperCase() || 'FREE'}
                </div>
                <div style={{ fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b' }}>
                  Last updated: {update.lastUpdated?.toLocaleString?.() || 'N/A'}
                </div>
              </div>
            </div>

            {update.coachingState && (
              <div style={{
                padding: 12,
                background: darkMode ? '#0f172a' : '#f8fafc',
                borderRadius: 8,
                fontSize: 13
              }}>
                <strong>Coaching Progress:</strong>
                <div style={{ marginTop: 4 }}>
                  Block: {update.coachingState.currentBlock || 'Not started'}<br/>
                  Stage: {update.coachingState.conversationStage || 'N/A'}
                </div>
              </div>
            )}

            {update.usage && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 8,
                marginTop: 12,
                fontSize: 12
              }}>
                <div>
                  <strong>Conversations:</strong> {update.usage.conversationCount || 0}
                </div>
                <div>
                  <strong>Text Tokens:</strong> {update.usage.textTokensUsed || 0}
                </div>
                <div>
                  <strong>Voice Chars:</strong> {update.usage.voiceCharactersUsed || 0}
                </div>
              </div>
            )}
          </div>
        ))}

        {userUpdates.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: darkMode ? '#94a3b8' : '#64748b' }}>
            No recent activity
          </div>
        )}
      </div>
    </div>
  );
}

// Support Tab Component (Old - keep for backward compatibility)
function SupportTab({ supportTickets, markSupportTicketResolved, darkMode }) {
  const [filter, setFilter] = useState('all');
  
  const filteredTickets = supportTickets.filter(ticket => {
    if (filter === 'open') return ticket.status !== 'resolved';
    if (filter === 'resolved') return ticket.status === 'resolved';
    return true;
  });

  return (
    <div>
      {/* Filter Buttons */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: filter === 'all' ? '2px solid #6366f1' : `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
            background: filter === 'all' ? '#6366f1' : (darkMode ? '#1e293b' : 'white'),
            color: filter === 'all' ? 'white' : (darkMode ? '#e2e8f0' : '#1e293b'),
            cursor: 'pointer',
            fontWeight: filter === 'all' ? 600 : 400,
          }}
        >
          All ({supportTickets.length})
        </button>
        <button
          onClick={() => setFilter('open')}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: filter === 'open' ? '2px solid #f59e0b' : `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
            background: filter === 'open' ? '#f59e0b' : (darkMode ? '#1e293b' : 'white'),
            color: filter === 'open' ? 'white' : (darkMode ? '#e2e8f0' : '#1e293b'),
            cursor: 'pointer',
            fontWeight: filter === 'open' ? 600 : 400,
          }}
        >
          Open ({supportTickets.filter(t => t.status !== 'resolved').length})
        </button>
        <button
          onClick={() => setFilter('resolved')}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: filter === 'resolved' ? '2px solid #10b981' : `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
            background: filter === 'resolved' ? '#10b981' : (darkMode ? '#1e293b' : 'white'),
            color: filter === 'resolved' ? 'white' : (darkMode ? '#e2e8f0' : '#1e293b'),
            cursor: 'pointer',
            fontWeight: filter === 'resolved' ? 600 : 400,
          }}
        >
          Resolved ({supportTickets.filter(t => t.status === 'resolved').length})
        </button>
      </div>

      {/* Support Tickets */}
      <div style={{ display: 'grid', gap: 16 }}>
        {filteredTickets.length === 0 ? (
          <div style={{
            padding: 40,
            textAlign: 'center',
            background: darkMode ? '#1e293b' : 'white',
            borderRadius: 12,
            color: darkMode ? '#94a3b8' : '#64748b',
          }}>
            No support tickets {filter !== 'all' ? `(${filter})` : ''}
          </div>
        ) : (
          filteredTickets.map((ticket) => (
            <div
              key={ticket.id}
              style={{
                padding: 24,
                background: darkMode ? '#1e293b' : 'white',
                borderRadius: 12,
                border: `2px solid ${ticket.status === 'resolved' ? '#10b981' : '#f59e0b'}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                      {ticket.subject || 'Support Request'}
                    </h3>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 600,
                      background: ticket.status === 'resolved' ? '#10b981' : '#f59e0b',
                      color: 'white',
                    }}>
                      {ticket.status === 'resolved' ? '✅ Resolved' : '🔔 Open'}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: darkMode ? '#94a3b8' : '#64748b', marginBottom: 4 }}>
                    <strong>From:</strong> {ticket.userEmail || ticket.userName || 'Anonymous'}
                  </div>
                  <div style={{ fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b' }}>
                    {ticket.timestamp?.toDate?.().toLocaleString() || 'Recently'}
                  </div>
                </div>
                {ticket.status !== 'resolved' && (
                  <button
                    onClick={() => markSupportTicketResolved(ticket.id)}
                    style={{
                      padding: '8px 16px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Mark Resolved
                  </button>
                )}
              </div>

              {/* Message/Description */}
              <div style={{
                padding: 16,
                background: darkMode ? '#0f172a' : '#f8fafc',
                borderRadius: 8,
                marginBottom: 12,
              }}>
                <p style={{
                  margin: 0,
                  lineHeight: 1.6,
                  fontSize: 14,
                  color: darkMode ? '#e2e8f0' : '#1e293b',
                  whiteSpace: 'pre-wrap',
                }}>
                  {ticket.message || ticket.description || 'No message provided'}
                </p>
              </div>

              {/* Category/Type */}
              {ticket.category && (
                <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b' }}>
                  <strong>Category:</strong> {ticket.category}
                </div>
              )}

              {/* Resolved Info */}
              {ticket.status === 'resolved' && ticket.resolvedAt && (
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  background: darkMode ? '#0f172a' : '#f0fdf4',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#10b981',
                }}>
                  ✅ Resolved on {new Date(ticket.resolvedAt).toLocaleString()}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Helper Functions
const getTierColor = (tier) => {
  switch (tier?.toLowerCase()) {
    case 'advisor': return '#6366f1';
    case 'coach': return '#8b5cf6';
    case 'mentor': return '#f59e0b';
    default: return '#94a3b8';
  }
};

const tableHeaderStyle = {
  padding: 16,
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 14,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tableCellStyle = {
  padding: 16,
  fontSize: 14,
};

const inputStyle = (darkMode) => ({
  padding: 12,
  borderRadius: 8,
  border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
  background: darkMode ? '#0f172a' : 'white',
  color: darkMode ? 'white' : '#0f172a',
  fontSize: 14,
});
